// Oireachtas stage schedule (card 524449bc, live stage display plan Phase 1).
// Forked from the feis Schedule page (ManageFeisGrade/RedirectFeis/Schedule/
// ManageSchedule.jsx): the same board, autosave and Undo, with:
//   - a day switcher (the event's days); stages belong to a day
//   - one card per competition ROUND: a championship's hard-shoe, soft-shoe and
//     set rounds are placed apart, as on the ECRO stage schedule
//   - block headings ("Solo Champ Hard Shoe") drawn over runs of like cards
//   - notes ("Recalls posted", "Awards") instead of breaks
//   - the whole schedule saves at once with the revision it was loaded at; if
//     someone else (organizer or admin) saved in between, the save is refused
//     and the page asks to reload rather than overwrite them.
// Devin 2026-10-07: the event's organizer and admins both build it. The admin
// app carries a copy (back link and import paths differ): keep the two in step.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import HeaderOrg from '../../Headers/HeaderOrg';
import Loader from '../../../Components/Loader/Loader';
import { useMaterialSymbols } from '../../../Teacher/Oireachtas/components';
import { clockTime, inputTime, longDuration, parseTime, shortDuration, shortTime } from '../../ManageFeisGrade/RedirectFeis/Schedule/scheduleTiming';
import { estimateCard } from './oireachtasTiming';
import {
  DEFAULT_NOTE,
  DEFAULT_START,
  NOTE_MAX,
  NOTE_MIN,
  atATimeLine,
  buildBoard,
  cardKey,
  cardOf,
  countFigure,
  countLine,
  csvRows,
  dayLabel,
  entrantsOf,
  groupPool,
  headingChips,
  headingsOf,
  itemKeyOf,
  itemMinutes,
  matchesCard,
  sexChips,
  sexOf,
  newId,
  savePayload,
  stageTimes,
  toCsv,
} from './oireachtasScheduleModel';
import { getSchedule, isConflict, saveSchedule } from './scheduleApi';
import '../../../Teacher/Oireachtas/oireachtas.css';
import '../organizerOireachtas.css';
import '../../ManageFeisGrade/RedirectFeis/Schedule/schedule.css';
import './oireachtasSchedule.css';

const BACK = (id) => `/organizer-oireachtas/${id}`;
const BACK_LABEL = 'Organizer panel';
const HOME = '/organizer-dashboard'; // no event chosen: back to the event list (admin review R1-F1)
const POOL = 'pool';
// Saves of one event's schedule run one after another, also across visits to
// the page: a visit's last save finishes before the next visit loads.
const writes = {};
const queueWrite = (eventId, run) => {
  const next = (writes[eventId] || Promise.resolve()).then(run, run);
  writes[eventId] = next.catch(() => {});
  return next;
};

const SAVE_AFTER_MS = 800; // a change saves this long after the last one
const RETRY_MS = 5000; // a failed save tries again after this
const UNDO_LIMIT = 50;
const COALESCE_MS = 1500; // typing in one field is one undo step
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
const visibleFocusables = (root) => [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
const menuPlace = (header) => {
  const r = header?.getBoundingClientRect();
  return r ? { top: r.top + 44, left: r.left + 8, width: Math.max(220, r.width - 16) } : null;
};
const messageOf = (err) => err?.messages || err?.message || 'Something went wrong. Please try again.';

const Msr = ({ children }) => (
  <span className="msr" aria-hidden="true">
    {children}
  </span>
);

// One competition round or note: code / start / minutes, then the round,
// the competition and its count, then dancers at a time.
const RowBody = ({ item, card, comp, time, minutes, pool }) => {
  if (item.type === 'note') {
    const notes = [item.fixed ? 'fixed start' : '', time?.gap ? `${time.gap} min gap before` : ''].filter(Boolean).join(' · ');
    return (
      <>
        <div className="sch-lc">
          <span className="sch-code" />
          {!pool && <span className={`sch-tm${item.fixed ? ' fixed' : ''}`}>{shortTime(time.start)}</span>}
          <span className="sch-du">{shortDuration(minutes)}</span>
        </div>
        <div className="sch-rc">
          <div className="sch-lv">Note</div>
          <div className="sch-nm">{item.label || 'Note'}</div>
          {(notes || time?.overlap > 0) && (
            <div className={`sch-mt${time?.overlap > 0 ? ' warn' : ''}`}>
              {time?.overlap > 0 ? `Starts ${time.overlap} min before the one before ends` : notes}
            </div>
          )}
        </div>
      </>
    );
  }
  const problem = item.problem === 'missing' ? 'Competition or round no longer exists · remove' : item.problem === 'duplicate' ? 'Scheduled twice · remove' : '';
  const k = card ? estimateCard(card, entrantsOf(comp), item.atATime).k : 0;
  const extra = [time?.gap ? `${time.gap} min gap before` : '', time?.overlap > 0 ? `starts ${time.overlap} min before the one before ends` : ''].filter(Boolean);
  return (
    <>
      <div className="sch-lc">
        <span className="sch-code">{comp?.code || item.code}</span>
        {!pool && <span className={`sch-tm${item.fixed ? ' fixed' : ''}`}>{shortTime(time.start)}</span>}
        <span className="sch-du">{shortDuration(minutes)}</span>
      </div>
      <div className="sch-rc">
        <div className="sch-lv">{card?.label || `Round ${item.round}`}</div>
        <div className="sch-nm">
          {comp?.name || item.code}
        </div>
        {problem ? (
          <div className="sch-mt warn">
            <Msr>error</Msr> {problem}
          </div>
        ) : (
          <div className={`sch-mt${time?.overlap > 0 ? ' warn' : ''}`}>{[card ? atATimeLine(card, k) : '', ...extra].filter(Boolean).join(' · ')}</div>
        )}
      </div>
      {/* the dancer count, large, in its own column (Devin 2026-10-07) */}
      {comp && card && !item.problem && (
        <div className="sch-num">
          <b>{countFigure(card, comp).value}</b>
          {countFigure(card, comp).label && <span>{countFigure(card, comp).label}</span>}
        </div>
      )}
    </>
  );
};

// Saved / Saving… / Couldn't save (it keeps trying) / changed elsewhere
const SaveStatus = ({ state, dirty, onRetry, short }) => {
  if (state === 'conflict') {
    return (
      <span className="sch-state is-error" role="status">
        <Msr>sync_problem</Msr>
        {short ? 'Changed elsewhere' : 'Changed elsewhere: not saved'}
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="sch-state is-error" role="status">
        <Msr>error</Msr>
        {short ? 'Not saved' : "Couldn't save, trying again"}
        <button type="button" className="sch-link" onClick={onRetry}>
          Retry
        </button>
      </span>
    );
  }
  const busy = state === 'saving' || dirty;
  return (
    <span className={`sch-state${busy ? '' : ' is-saved'}`} role="status">
      <Msr>{busy ? 'sync' : 'cloud_done'}</Msr>
      {busy ? 'Saving…' : 'Saved'}
    </span>
  );
};

const Stepper = ({ value, min = 1, max = 6, step = 1, onChange, label }) => (
  <span className="sch-stepper" role="group" aria-label={label}>
    <button type="button" aria-label={`Fewer: ${label}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - step))}>
      −
    </button>
    <b>{value}</b>
    <button type="button" aria-label={`More: ${label}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + step))}>
      +
    </button>
  </span>
);

// Saves that could not land after the page was left are reported on the next
// visit (code review R1-F1).
const lostKey = (eventId) => `oireachtasScheduleLost:${eventId}`;
const markLost = (eventId, why) => {
  try {
    sessionStorage.setItem(lostKey(eventId), why);
  } catch (e) {
    /* storage unavailable: nothing to report next time */
  }
};
const takeLost = (eventId) => {
  try {
    const why = sessionStorage.getItem(lostKey(eventId));
    sessionStorage.removeItem(lostKey(eventId));
    return why;
  } catch (e) {
    return null;
  }
};

const ScheduleEditor = ({ eventId }) => {
  useMaterialSymbols();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [comps, setComps] = useState({});
  const [cards, setCards] = useState({});
  const [order, setOrder] = useState([]); // every card key, syllabus order
  const [days, setDays] = useState([]);
  const [day, setDay] = useState(null);
  const [board, setBoard] = useState({}); // day -> stages
  const [pool, setPool] = useState([]); // card keys not on any stage
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState('saved'); // saved | saving | error | conflict
  const [undo, setUndo] = useState([]); // earlier { board, pool }, newest last
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadNote, setLoadNote] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sex, setSex] = useState('all'); // Girls / Boys / Mixed pills
  const [selected, setSelected] = useState(null); // { where: stage id | 'pool', key }
  const [menuAt, setMenuAt] = useState(null);
  const menuAnchor = useRef(null);
  const [menu, setMenu] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [view, setView] = useState(POOL); // phone: 'pool' or a stage id
  const pageRef = useRef(null);
  const revision = useRef(0);

  const stages = useMemo(() => (day && board[day]) || [], [board, day]);

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current; // only the latest load may set the board (admin review R1-F2)
    setLoading(true);
    setLoadError('');
    try {
      await writes[eventId]; // a save from the last visit lands first
      const res = await getSchedule(eventId);
      if (seq !== loadSeq.current) return;
      const lost = takeLost(eventId);
      const b = buildBoard(res?.competitions || [], res?.schedule, res?.event?.days || []);
      revision.current = b.revision;
      setEvent(res?.event || null);
      setComps(b.comps);
      setCards(b.cards);
      setOrder(b.order);
      setDays(b.days);
      setDay((d) => (d && b.days.includes(d) ? d : b.days[0] || null));
      setBoard(b.board);
      setPool(b.pool);
      setLoadNote(
        [
          lost === 'conflict' ? 'Your last changes before you left this page were not saved: someone else had changed the schedule. Please check it and make them again.' : '',
          lost === 'error' ? 'Your last changes before you left this page could not be saved. Please check the schedule and make them again.' : '',
          b.dropped ? `${b.dropped} round${b.dropped === 1 ? ' was' : 's were'} on a day the event no longer has and went back to To schedule.` : '',
        ]
          .filter(Boolean)
          .join(' ')
      );
      setDirty(Boolean(b.dropped));
      setSaveState('saved');
      setUndo([]);
      setSelected(null);
      setView(POOL);
    } catch (err) {
      if (seq === loadSeq.current) setLoadError(messageOf(err));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      load();
    } else {
      // opened without an event (admin review R1-F1)
      setLoading(false);
      setLoadError('No event was chosen. Open the stage schedule from the event.');
    }
  }, [eventId, load]);

  // the body takes the page's background while this page is open
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const before = document.body.style.backgroundColor;
    document.body.style.backgroundColor = getComputedStyle(page).backgroundColor;
    return () => {
      document.body.style.backgroundColor = before;
    };
  }, []);

  // ---- autosave -------------------------------------------------------------
  // Every change saves itself a moment after the last one; one save at a time,
  // a failed save keeps trying, and a save refused because someone else saved
  // first stops until the page is reloaded (it never overwrites them).
  const latest = useRef({ board: {}, days: [], cards: {}, comps: {} });
  latest.current = { board, days, cards, comps };
  const inFlight = useRef(false);
  const again = useRef(false);
  const timer = useRef(null);
  const alive = useRef(true);
  const conflicted = useRef(false);
  const saveNow = useCallback(async () => {
    clearTimeout(timer.current);
    if (conflicted.current) return;
    if (inFlight.current) {
      again.current = true;
      return;
    }
    inFlight.current = true;
    setSaveState('saving');
    const snap = latest.current;
    try {
      // the body is built when its turn in the queue comes, with the revision
      // the save before it returned (code review R1-F1)
      const res = await queueWrite(eventId, () => saveSchedule(eventId, savePayload(revision.current, snap.days, snap.board, snap.cards, snap.comps)));
      revision.current = Number(res?.schedule?.revision) || revision.current + 1;
      setDirty((d) => (latest.current.board === snap.board ? false : d));
      setSaveState('saved');
    } catch (err) {
      if (isConflict(err)) {
        conflicted.current = true;
        setSaveState('conflict');
        setSelected(null); // nothing can change until the page is reloaded
        setMenu(null);
      } else {
        setSaveState('error');
        if (alive.current) timer.current = setTimeout(() => saveNow(), RETRY_MS);
      }
    } finally {
      inFlight.current = false;
      if (again.current && alive.current && !conflicted.current) {
        again.current = false;
        saveNow();
      }
    }
  }, [eventId]);
  // An edit (re)starts the short wait. Save-status changes don't, so a failed
  // save keeps its longer retry wait (code review R1-F2).
  useEffect(() => {
    if (!dirty || conflicted.current) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => saveNow(), SAVE_AFTER_MS);
    return () => clearTimeout(timer.current);
  }, [dirty, board, saveNow]);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    alive.current = true;
    conflicted.current = false;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
      again.current = false;
      // the last change goes into the event's write queue on the way out
      // Built when its turn comes (after any save still running), and skipped
      // if that save was refused; a refused or failed last save is reported on
      // the next visit (code review R1-F1).
      const snap = latest.current;
      if (dirtyRef.current && !conflicted.current && snap.days.length) {
        queueWrite(eventId, () => {
          if (conflicted.current) {
            markLost(eventId, 'conflict');
            return null;
          }
          return saveSchedule(eventId, savePayload(revision.current, snap.days, snap.board, snap.cards, snap.comps)).then(
            (res) => {
              revision.current = Number(res?.schedule?.revision) || revision.current;
            },
            (err) => markLost(eventId, isConflict(err) ? 'conflict' : 'error')
          );
        });
      }
    };
  }, [eventId]);
  useEffect(() => {
    if (!dirty && saveState !== 'error' && saveState !== 'conflict') return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saveState]);

  const reload = () => {
    conflicted.current = false;
    load();
  };

  // the fixed stage menu follows its stage header
  useEffect(() => {
    if (!menu) return undefined;
    const follow = () => setMenuAt(menuPlace(menuAnchor.current));
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    return () => {
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
    };
  }, [menu]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setSelected(null);
      setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const times = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, stageTimes(s, cards, comps)])), [stages, cards, comps]);

  // ---- changes and Undo ------------------------------------------------------
  const before = useRef({ board: {}, pool: [] });
  before.current = { board, pool };
  const lastStep = useRef({ key: null, at: 0, tick: null });
  const tick = useRef(0);
  useEffect(() => {
    tick.current += 1; // one render = one action
  });
  const snapshot = (coalesce) => {
    const now = Date.now();
    const last = lastStep.current;
    const sameAction = last.tick === tick.current;
    const sameField = coalesce && last.key === coalesce && now - last.at < COALESCE_MS;
    lastStep.current = { key: coalesce || null, at: now, tick: tick.current };
    if (sameAction || sameField) return;
    const snap = before.current;
    setUndo((u) => [...u, snap].slice(-UNDO_LIMIT));
  };
  // a change to the day on screen
  // after a refused save nothing changes until the page is reloaded - the
  // buttons, Ctrl+Z and any open control alike (code review R1-F5)
  const change = (fn, coalesce) => {
    if (!day || conflicted.current) return;
    snapshot(coalesce);
    setBoard((prev) => ({ ...prev, [day]: fn(prev[day] || []) }));
    setDirty(true);
  };
  const changeItem = (stageId, key, patch, coalesce) =>
    change((prev) => prev.map((s) => (s.id !== stageId ? s : { ...s, items: s.items.map((it) => (it.key === key ? { ...it, ...patch } : it)) })), coalesce);
  const undoLast = () => {
    if (!undo.length || conflicted.current) return;
    const prev = undo[undo.length - 1];
    setUndo((u) => u.slice(0, -1));
    lastStep.current = { key: null, at: 0, tick: null };
    setBoard(prev.board);
    setPool(prev.pool);
    setSelected(null);
    setMenu(null);
    setDirty(true);
  };
  const undoRef = useRef(undoLast);
  undoRef.current = undoLast;
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || String(e.key).toLowerCase() !== 'z') return;
      if (e.target.closest && e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      undoRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // the To schedule list keeps syllabus order
  const rank = useMemo(() => new Map(order.map((k, i) => [k, i])), [order]);
  const backToPool = (keys) => setPool((p) => [...new Set([...p, ...keys])].sort((a, b) => (rank.get(a) ?? 0) - (rank.get(b) ?? 0)));

  // ---- moving things -------------------------------------------------------
  const moveItem = (fromId, key, toId, toIndex) => {
    change((prev) => {
      const moving = prev.find((s) => s.id === fromId)?.items.find((it) => it.key === key);
      if (!moving) return prev;
      const without = prev.map((s) => (s.id !== fromId ? s : { ...s, items: s.items.filter((it) => it.key !== key) }));
      return without.map((s) => {
        if (s.id !== toId) return s;
        const items = [...s.items];
        items.splice(toIndex == null ? items.length : toIndex, 0, moving);
        return { ...s, items };
      });
    });
    if (selected?.key === key) setSelected({ where: toId, key });
  };

  const unschedule = (stageId, key) => {
    if (conflicted.current) return;
    const item = stages.find((s) => s.id === stageId)?.items.find((it) => it.key === key);
    if (!item) return;
    change((prev) => prev.map((s) => (s.id !== stageId ? s : { ...s, items: s.items.filter((it) => it.key !== key) })));
    if (item.type === 'round' && !item.problem) {
      const ck = cardKey(item.competitionId, item.round);
      if (cards[ck]) backToPool([ck]);
    }
    setSelected(null);
  };

  const schedule = (ck, stageId, index) => {
    if (conflicted.current) return;
    const card = cards[ck];
    if (!card || !stages.some((s) => s.id === stageId)) return;
    setPool((p) => p.filter((k) => k !== ck));
    change((prev) =>
      prev.map((s) => {
        if (s.id !== stageId) return s;
        const items = [...s.items];
        const item = { key: itemKeyOf(card.compId, card.round), type: 'round', competitionId: card.compId, round: card.round, code: comps[card.compId]?.code || '', atATime: null, fixed: false, fixedAt: null, problem: null };
        items.splice(index == null ? items.length : index, 0, item);
        return { ...s, items };
      })
    );
    setSelected(null);
  };

  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;
    const from = source.droppableId;
    const to = destination.droppableId;
    if (from === POOL && to === POOL) return;
    if (from === POOL) {
      schedule(draggableId.replace(/^card:/, ''), to, destination.index);
      return;
    }
    if (to === POOL) {
      unschedule(from, draggableId);
      return;
    }
    if (from === to && source.index === destination.index) return;
    moveItem(from, draggableId, to, destination.index);
  };

  // ---- stages (local; saved with the schedule) --------------------------------
  const addStage = () => {
    if (!day) return;
    // Stage A, Stage B, ... as on the ECRO schedule (renamable)
    const used = new Set(stages.map((s) => s.name.trim().toLowerCase()));
    let n = stages.length;
    const nameOf = (i) => `Stage ${i < 26 ? String.fromCharCode(65 + i) : i + 1}`;
    while (used.has(nameOf(n).toLowerCase())) n += 1;
    const id = newId('stage');
    change((prev) => [...prev, { id, name: nameOf(n), startMin: DEFAULT_START, items: [] }]);
    setView(id);
  };

  const deleteStage = (stageId) => {
    if (conflicted.current) return;
    const stage = stages.find((s) => s.id === stageId);
    if (!stage) return;
    const back = stage.items.filter((it) => it.type === 'round' && !it.problem && cards[cardKey(it.competitionId, it.round)]).map((it) => cardKey(it.competitionId, it.round));
    change((prev) => prev.filter((s) => s.id !== stageId));
    backToPool(back);
    setMenu(null);
    setConfirmDelete(false);
    if (selected?.where === stageId) setSelected(null);
    setView(POOL);
  };

  const renameStage = (stageId, name) => change((prev) => prev.map((s) => (s.id === stageId ? { ...s, name } : s)), `name-${stageId}`);
  const setStageStart = (stageId, value) => {
    const t = parseTime(value);
    if (t == null) return;
    change((prev) => prev.map((s) => (s.id === stageId ? { ...s, startMin: t } : s)), `start-${stageId}`);
  };

  const targetStage = () => (selected && selected.where !== POOL ? selected.where : view && view !== POOL ? view : stages[0]?.id);

  const addNote = () => {
    const stageId = targetStage();
    if (!stageId) return;
    const key = newId('note');
    change((prev) => prev.map((s) => (s.id !== stageId ? s : { ...s, items: [...s.items, { key, type: 'note', label: 'Note', minutes: DEFAULT_NOTE, fixed: false, fixedAt: null }] })));
    setSelected({ where: stageId, key });
    setView(stageId);
  };

  const switchDay = (d) => {
    setDay(d);
    setSelected(null);
    setMenu(null);
    setView(POOL);
  };

  const exportCsv = () => {
    const blob = new Blob([`﻿${toCsv(csvRows(days, board, cards, comps))}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Stage schedule - ${String(event?.name || 'Oireachtas').replace(/[\\/:*?"<>|]/g, '')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };

  const back = () => navigate(eventId ? BACK(eventId) : HOME);

  // ---- derived -----------------------------------------------------------
  const poolKeys = useMemo(
    () =>
      pool.filter(
        (k) =>
          cards[k] &&
          (filter === 'all' || (cards[k].heading || 'Other') === filter) &&
          (sex === 'all' || sexOf(comps[cards[k].compId]) === sex) &&
          matchesCard(cards[k], comps[cards[k].compId], query)
      ),
    [pool, cards, comps, filter, sex, query]
  );
  const poolGroups = useMemo(() => groupPool(poolKeys, cards), [poolKeys, cards]);
  const chips = useMemo(() => headingChips(order, pool, cards), [order, pool, cards]);
  const sexes = useMemo(() => sexChips(order, pool, cards, comps), [order, pool, cards, comps]);
  const roundsOnDay = (d) => (board[d] || []).reduce((n, s) => n + s.items.filter((it) => it.type === 'round' && !it.problem).length, 0);

  const subtitle = event
    ? [event.name, `${days.length} day${days.length === 1 ? '' : 's'}`, `${order.length} competition round${order.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')
    : '';

  const sel = useMemo(() => {
    if (!selected) return null;
    if (selected.where === POOL) {
      const card = cards[selected.key];
      return card ? { where: POOL, card, comp: comps[card.compId], item: { key: `card:${card.key}`, type: 'round', competitionId: card.compId, round: card.round, atATime: null } } : null;
    }
    const stage = stages.find((s) => s.id === selected.where);
    const index = stage ? stage.items.findIndex((it) => it.key === selected.key) : -1;
    if (index < 0) return null;
    const item = stage.items[index];
    return { where: stage.id, stage, item, index, card: cardOf(item, cards), comp: item.type === 'round' ? comps[item.competitionId] : null, time: times[stage.id]?.[index] };
  }, [selected, stages, cards, comps, times]);

  const q = query.trim();
  const hit = (item) => {
    if (!q) return false;
    if (item.type === 'note') return String(item.label || '').toLowerCase().includes(q.toLowerCase());
    const card = cardOf(item, cards);
    return card ? matchesCard(card, comps[item.competitionId], q) : false;
  };

  const openOnEnter = (e, target) => {
    if (e.key !== 'Enter' || e.target !== e.currentTarget) return;
    e.preventDefault();
    setSelected(target);
  };

  // The timing panel is modal for keyboards too: focus moves in, Tab stays
  // inside, the page behind is inert, closing returns focus to the row.
  const panelRef = useRef(null);
  const panelOpen = Boolean(sel);
  const selectedKey = useRef(null);
  selectedKey.current = selected?.key || null;
  useEffect(() => {
    if (!panelOpen) return undefined;
    const opener = document.activeElement;
    const openedKey = selectedKey.current;
    const page = pageRef.current;
    const inPage = page ? [...page.children].filter((el) => !el.classList.contains('sch-panel') && !el.classList.contains('sch-scrim')) : [];
    const beside = page?.parentElement ? [...page.parentElement.children].filter((el) => el !== page) : [];
    const behind = [...inPage, ...beside].map((el) => ({ el, inert: el.getAttribute('inert'), hidden: el.getAttribute('aria-hidden') }));
    behind.forEach(({ el }) => {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    });
    const first = panelRef.current ? visibleFocusables(panelRef.current)[0] : null;
    if (first) first.focus();
    const onKey = (e) => {
      if (e.key !== 'Tab' || !panelRef.current) return;
      const all = visibleFocusables(panelRef.current);
      if (!all.length) return;
      const head = all[0];
      const tail = all[all.length - 1];
      if (e.shiftKey && (document.activeElement === head || !panelRef.current.contains(document.activeElement))) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && (document.activeElement === tail || !panelRef.current.contains(document.activeElement))) {
        e.preventDefault();
        head.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      behind.forEach(({ el, inert, hidden }) => {
        if (inert == null) el.removeAttribute('inert');
        else el.setAttribute('inert', inert);
        if (hidden == null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', hidden);
      });
      const row = openedKey ? page?.querySelector(`.sch-row[data-key="${CSS.escape(openedKey)}"]`) || page?.querySelector(`.sch-row[data-key="${CSS.escape(`card:${openedKey}`)}"]`) : null;
      const backTo = row || (opener && document.contains(opener) && opener !== document.body ? opener : null);
      if (backTo && typeof backTo.focus === 'function') backTo.focus();
    };
  }, [panelOpen]);

  const renderPanel = () => {
    if (!sel) return null;
    const { item, card, comp, where } = sel;
    const others = stages.filter((s) => s.id !== where);
    const isNote = item.type === 'note';
    const est = card && comp && !item.problem && !isNote ? estimateCard(card, entrantsOf(comp), item.atATime) : null;
    const fixedValue = inputTime(item.fixed && Number.isFinite(item.fixedAt) ? item.fixedAt : sel.time?.start ?? DEFAULT_START);
    return (
      <div className="sch-panel" role="dialog" aria-modal="true" aria-label="Timing" ref={panelRef}>
        <button type="button" className="sch-x" aria-label="Close" onClick={() => setSelected(null)}>
          <Msr>close</Msr>
        </button>
        {isNote ? (
          <>
            <div className="sch-pt">Note</div>
            {sel.time && (
              <div className="sch-psub">
                {sel.stage.name} · {clockTime(sel.time.start)} – {clockTime(sel.time.end)}
              </div>
            )}
            <label className="sch-field">
              <span>Text</span>
              <input type="text" maxLength={60} value={item.label} onChange={(e) => changeItem(where, item.key, { label: e.target.value }, `label-${item.key}`)} />
            </label>
            <div className="sch-field">
              <span>Length</span>
              <span className="sch-len">
                {/* any whole minute 1-600 (code review 51f36ce7 R1-F3: a 5-minute stepper skipped 2-4) */}
                <input
                  type="number"
                  className="osch-minutes"
                  aria-label="Note length in minutes"
                  min={NOTE_MIN}
                  max={NOTE_MAX}
                  step={1}
                  value={item.minutes}
                  onChange={(e) => {
                    const v = Math.round(Number(e.target.value));
                    if (Number.isFinite(v) && v >= NOTE_MIN && v <= NOTE_MAX) changeItem(where, item.key, { minutes: v }, `minutes-${item.key}`);
                  }}
                />
                <span>min · {longDuration(item.minutes)}</span>
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="sch-pt">
              {comp?.code || item.code}
              {card ? ` · ${card.label}` : ''}
            </div>
            <div className="sch-psub">
              {comp ? `${comp.name} · ${countLine(card, comp)}` : ''}
              {sel.time ? ` · ${sel.stage.name}, ${clockTime(sel.time.start)} – ${clockTime(sel.time.end)}` : ''}
            </div>
            {item.problem ? (
              <p className="sch-warn">
                {item.problem === 'duplicate'
                  ? 'This round is on the schedule twice. Remove this one: it is left out when you save.'
                  : 'This competition, or this round of it, no longer exists. Remove it: it is left out when you save.'}
              </p>
            ) : (
              est && (
                <>
                  <div className="sch-big">
                    <b>{longDuration(est.minutes)}</b>
                  </div>
                  <table className="sch-rounds">
                    <thead>
                      <tr>
                        <th>Dance</th>
                        <th>At a time</th>
                        <th className="n">Groups</th>
                        <th className="n">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          {card.label}
                          <div className="sch-note">{est.music}</div>
                        </td>
                        <td>
                          {where === POOL || est.continuous ? (
                            est.continuous ? 'one after another' : est.k
                          ) : (
                            <Stepper label="dancers at a time" value={est.k} onChange={(k) => changeItem(where, item.key, { atATime: k })} />
                          )}
                        </td>
                        <td className="n">{est.groups}</td>
                        <td className="n">{longDuration(est.sec / 60)}</td>
                      </tr>
                      <tr>
                        <td colSpan={3}>
                          <span className="sch-note">{card.round > 1 ? 'Round change and recall' : 'Call and line-up'}</span>
                        </td>
                        <td className="n">{longDuration(est.overheadSec / 60)}</td>
                      </tr>
                    </tbody>
                  </table>
                  {card.choice?.length > 0 && <p className="sch-note">Each dancer chose: {card.choice.map((c) => c.name).join(' or ')}.</p>}
                  {card.note && (
                    <p className="sch-warn">
                      <Msr>info</Msr> {card.note}
                    </p>
                  )}
                </>
              )
            )}
          </>
        )}

        {where === POOL ? (
          <div className="sch-field sch-phone-only">
            <span>Add to a stage</span>
            <div className="sch-acts">
              {stages.map((s) => (
                <button key={s.id} type="button" className="op-btn" onClick={() => schedule(card.key, s.id)}>
                  {s.name}
                </button>
              ))}
              {!stages.length && <span className="sch-note">Add a stage first.</span>}
            </div>
          </div>
        ) : (
          <>
            {!item.problem && (
              <div className="sch-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={!!item.fixed}
                    onChange={(e) => changeItem(where, item.key, { fixed: e.target.checked, fixedAt: e.target.checked ? parseTime(fixedValue) : item.fixedAt })}
                  />
                  <span>
                    Start at a fixed time
                    <span className="sch-note">Otherwise it starts when the one before ends</span>
                  </span>
                </label>
                {item.fixed && (
                  <input
                    type="time"
                    aria-label="Fixed start time"
                    value={fixedValue}
                    onChange={(e) => {
                      const t = parseTime(e.target.value);
                      if (t != null) changeItem(where, item.key, { fixedAt: t }, `fixed-${item.key}`);
                    }}
                  />
                )}
              </div>
            )}
            <div className="sch-acts sch-pfoot">
              {!item.problem && others.length > 0 && (
                <label className="sch-move sch-phone-only">
                  <span>Move to</span>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) moveItem(where, item.key, e.target.value);
                    }}
                  >
                    <option value="">Stage…</option>
                    {others.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button type="button" className="op-btn" onClick={() => unschedule(where, item.key)}>
                <Msr>{isNote || item.problem ? 'delete' : 'undo'}</Msr>
                {isNote || item.problem ? 'Remove' : 'Unschedule'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  let poolIndex = 0;

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page sch-page osch-page" ref={pageRef}>
        {loading && <Loader />}
        <div className="wrap">
          <button type="button" className="op-back" onClick={back}>
            <Msr>arrow_back</Msr>
            {BACK_LABEL}
          </button>
          <div className="sch-ph1">
            <div>
              <div className="sch-h1" role="heading" aria-level={1}>
                Stage schedule
              </div>
              <div className="sch-sub">{subtitle || ' '}</div>
            </div>
            <div className="sch-top-acts">
              <SaveStatus state={saveState} dirty={dirty} onRetry={saveNow} />
              <button type="button" className="op-btn" onClick={undoLast} disabled={!undo.length || saveState === 'conflict'} title="Undo (Ctrl+Z)">
                <Msr>undo</Msr>Undo
              </button>
              <button type="button" className="op-btn" onClick={exportCsv} disabled={!order.length}>
                <Msr>download</Msr>Export CSV
              </button>
            </div>
          </div>

          {saveState === 'conflict' && (
            <div className="op-error osch-conflict" role="alert">
              <span>Someone else changed this schedule after you opened it. Your latest changes were not saved. Reload to see theirs, then make your changes again.</span>
              <button type="button" className="op-btn sm" onClick={reload}>
                <Msr>refresh</Msr>Reload
              </button>
            </div>
          )}
          {loadNote && <div className="sch-pdf">{loadNote}</div>}

          {days.length > 0 && (
            <div className="osch-days" role="tablist" aria-label="Day">
              {days.map((d) => (
                <button key={d} type="button" role="tab" aria-selected={day === d} className={`sch-fchip${day === d ? ' on' : ''}`} onClick={() => switchDay(d)}>
                  {dayLabel(d)} <span className="n">{roundsOnDay(d)}</span>
                </button>
              ))}
            </div>
          )}
          {!loading && !loadError && !days.length && (
            <div className="sch-empty">
              <Msr>event</Msr>
              <div className="sch-empty-t">The event has no days set</div>
              <div className="sch-empty-s">Ask Feis.link to set the event days, then build the schedule here.</div>
            </div>
          )}

          <div className="sch-bar">
            <label className="sch-search">
              <Msr>search</Msr>
              <input
                type="text"
                value={query}
                autoComplete="off"
                spellCheck={false}
                placeholder="Find a competition: code, name, age"
                aria-label="Find a competition"
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" className="sch-clear" aria-label="Clear search" onClick={() => setQuery('')}>
                  <Msr>close</Msr>
                </button>
              )}
            </label>
            <button type="button" className="op-btn" onClick={addNote} disabled={!stages.length || saveState === 'conflict'}>
              <Msr>sticky_note_2</Msr>Add note
            </button>
            <button type="button" className="op-btn" onClick={addStage} disabled={!day || saveState === 'conflict'}>
              <Msr>add</Msr>Add stage
            </button>
          </div>

          {loadError && (
            <div className="sch-empty">
              <Msr>error</Msr>
              <div className="sch-empty-t">The schedule didn’t load</div>
              <div className="sch-empty-s">{loadError}</div>
              <button type="button" className="op-btn" onClick={load}>
                Try again
              </button>
            </div>
          )}

          <div className="sch-pills" role="tablist" aria-label="Show">
            <button type="button" role="tab" aria-selected={view === POOL} className={`sch-pill${view === POOL ? ' on' : ''}`} onClick={() => setView(POOL)}>
              To schedule <span className="n">{pool.length}</span>
            </button>
            {stages.map((s) => {
              const t = times[s.id] || [];
              return (
                <button key={s.id} type="button" role="tab" aria-selected={view === s.id} className={`sch-pill${view === s.id ? ' on' : ''}`} onClick={() => setView(s.id)}>
                  {s.name}
                  {t.length > 0 && <span className="n">ends {shortTime(t[t.length - 1].end)}</span>}
                </button>
              );
            })}
          </div>

          {!loadError && day && (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className={`sch-board${saveState === 'conflict' ? ' osch-locked' : ''}`}>
                <Droppable droppableId={POOL} isDropDisabled={saveState === 'conflict'}>
                  {(provided, snapshot) => (
                    <div className={`sch-col sch-pool${view === POOL ? ' active' : ''}${snapshot.isDraggingOver ? ' over' : ''}`}>
                      <div className="sch-colh">
                        <div className="sch-colt">
                          <span className="sch-name">
                            <Msr>inbox</Msr>To schedule
                          </span>
                          <span className={`sch-count${pool.length ? '' : ' done'}`}>{pool.length ? `${pool.length} left` : 'All scheduled'}</span>
                        </div>
                        {/* one compact "Show" list: an Oireachtas has ~17 kinds of round, too many chips for a laptop screen */}
                        <label className="osch-filter">
                          <span>Show</span>
                          <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Show which rounds">
                            <option value="all">All ({pool.length} left)</option>
                            {chips.map((f) => (
                              <option key={f.key} value={f.key}>
                                {f.label} ({f.left} left)
                              </option>
                            ))}
                          </select>
                        </label>
                        {sexes.length > 1 && (
                          <div className="sch-filters osch-sexes" role="group" aria-label="Girls or boys">
                            <button type="button" className={`sch-fchip${sex === 'all' ? ' on' : ''}`} aria-pressed={sex === 'all'} onClick={() => setSex('all')}>
                              All
                            </button>
                            {sexes.map((x) => (
                              <button
                                key={x.key}
                                type="button"
                                className={`sch-fchip${sex === x.key ? ' on' : ''}${x.left ? '' : ' none-left'}`}
                                aria-pressed={sex === x.key}
                                onClick={() => setSex(sex === x.key ? 'all' : x.key)}
                              >
                                {x.label} <span className="n">{x.left}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="sch-list" ref={provided.innerRef} {...provided.droppableProps}>
                        {poolGroups.map((g) => (
                          <React.Fragment key={g.title}>
                            <div className="sch-grp">{g.title}</div>
                            {g.keys.map((k) => {
                              const card = cards[k];
                              const comp = comps[card.compId];
                              const index = poolIndex;
                              poolIndex += 1;
                              return (
                                <Draggable key={k} draggableId={`card:${k}`} index={index} isDragDisabled={saveState === 'conflict'}>
                                  {(p) => (
                                    <div
                                      ref={p.innerRef}
                                      {...p.draggableProps}
                                      {...p.dragHandleProps}
                                      data-key={`card:${k}`}
                                      className={`sch-row${selected?.where === POOL && selected.key === k ? ' sel' : ''}`}
                                      aria-label={`${comp?.code} ${card.label}: open its timing`}
                                      onClick={() => setSelected({ where: POOL, key: k })}
                                      onKeyDown={(e) => openOnEnter(e, { where: POOL, key: k })}
                                    >
                                      <RowBody
                                        item={{ type: 'round', competitionId: card.compId, round: card.round, code: comp?.code }}
                                        card={card}
                                        comp={comp}
                                        minutes={estimateCard(card, entrantsOf(comp)).minutes}
                                        pool
                                      />
                                      {comp?.day ? <div className="osch-hint">Syllabus day: {comp.day}</div> : null}
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                          </React.Fragment>
                        ))}
                        {provided.placeholder}
                        {!poolKeys.length && <div className="sch-none">{pool.length ? 'Nothing matches.' : 'Everything is on a stage.'}</div>}
                      </div>
                    </div>
                  )}
                </Droppable>

                <div className="sch-stages">
                  {stages.map((s) => {
                    const t = times[s.id] || [];
                    const heads = headingsOf(s.items, cards);
                    const count = s.items.filter((it) => it.type === 'round' && !it.problem).length;
                    return (
                      <Droppable key={s.id} droppableId={s.id} isDropDisabled={saveState === 'conflict'}>
                        {(provided, snapshot) => (
                          <div className={`sch-col${view === s.id ? ' active' : ''}${snapshot.isDraggingOver ? ' over' : ''}`} data-stage={s.name}>
                            <div className="sch-colh">
                              <div className="sch-colt">
                                <span className="sch-name">{s.name}</span>
                                <button
                                  type="button"
                                  className="sch-more"
                                  aria-label={`${s.name} options`}
                                  onClick={(e) => {
                                    menuAnchor.current = e.currentTarget.closest('.sch-colh');
                                    setMenuAt(menuPlace(menuAnchor.current));
                                    setMenu(menu === s.id ? null : s.id);
                                    setConfirmDelete(false);
                                  }}
                                >
                                  <Msr>more_horiz</Msr>
                                </button>
                              </div>
                              <div className="sch-span">
                                <b>{clockTime(t[0]?.start ?? s.startMin)}</b>
                                {t.length > 0 && (
                                  <>
                                    {' – '}
                                    <b>{clockTime(t[t.length - 1].end)}</b>
                                  </>
                                )}
                                {` · ${count} round${count === 1 ? '' : 's'}`}
                              </div>
                              {menu === s.id && (
                                <div className="sch-menu" style={menuAt ? { position: 'fixed', top: menuAt.top, left: menuAt.left, right: 'auto', width: menuAt.width } : undefined}>
                                  <label className="sch-field">
                                    <span>Name</span>
                                    <input type="text" maxLength={60} value={s.name} onChange={(e) => renameStage(s.id, e.target.value)} />
                                  </label>
                                  <label className="sch-field">
                                    <span>Starts at</span>
                                    <input type="time" value={inputTime(s.startMin)} onChange={(e) => setStageStart(s.id, e.target.value)} />
                                  </label>
                                  {confirmDelete ? (
                                    <div className="sch-acts">
                                      <span className="sch-note">Its rounds go back to To schedule. Undo brings the stage back.</span>
                                      <button type="button" className="op-btn sch-danger" onClick={() => deleteStage(s.id)}>
                                        Delete {s.name}
                                      </button>
                                      <button type="button" className="op-btn" onClick={() => setConfirmDelete(false)}>
                                        Keep
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="sch-acts">
                                      <button type="button" className="op-btn" onClick={() => setConfirmDelete(true)}>
                                        <Msr>delete</Msr>Delete stage
                                      </button>
                                      <button type="button" className="op-btn" onClick={() => setMenu(null)}>
                                        Done
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="sch-list" ref={provided.innerRef} {...provided.droppableProps}>
                              {s.items.map((item, i) => (
                                <React.Fragment key={item.key}>
                                  {heads[i] && <div className="sch-grp osch-block">{heads[i]}</div>}
                                  <Draggable draggableId={item.key} index={i} isDragDisabled={saveState === 'conflict'}>
                                    {(p) => (
                                      <div
                                        ref={p.innerRef}
                                        {...p.draggableProps}
                                        {...p.dragHandleProps}
                                        data-key={item.key}
                                        className={[
                                          'sch-row',
                                          item.type === 'note' ? 'brk' : '',
                                          item.problem ? 'bad' : '',
                                          selected?.where === s.id && selected.key === item.key ? 'sel' : '',
                                          hit(item) ? 'hit' : '',
                                        ]
                                          .filter(Boolean)
                                          .join(' ')}
                                        onClick={() => setSelected({ where: s.id, key: item.key })}
                                        onKeyDown={(e) => openOnEnter(e, { where: s.id, key: item.key })}
                                      >
                                        <RowBody item={item} card={cardOf(item, cards)} comp={item.type === 'round' ? comps[item.competitionId] : null} time={t[i]} minutes={itemMinutes(item, cards, comps)} />
                                        <button
                                          type="button"
                                          className="sch-rowx"
                                          aria-label={item.type === 'note' || item.problem ? `Remove ${item.type === 'note' ? item.label || 'note' : item.code || 'row'}` : `Unschedule ${item.code || comps[item.competitionId]?.code || ''}`}
                                          title={item.type === 'note' || item.problem ? 'Remove' : 'Back to To schedule'}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            unschedule(s.id, item.key);
                                          }}
                                          onKeyDown={(e) => e.stopPropagation()}
                                        >
                                          <Msr>close</Msr>
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                </React.Fragment>
                              ))}
                              {provided.placeholder}
                              {!s.items.length && <div className="sch-none">Drag competition rounds here</div>}
                            </div>
                          </div>
                        )}
                      </Droppable>
                    );
                  })}
                  {!stages.length && !loading && (
                    <div className="sch-empty">
                      <Msr>view_column</Msr>
                      <div className="sch-empty-t">No stages on {dayLabel(day, true)} yet</div>
                      <div className="sch-empty-s">Add a stage, then drag competition rounds onto it.</div>
                    </div>
                  )}
                </div>
              </div>
            </DragDropContext>
          )}
        </div>

        {sel && (
          <>
            <div className="sch-scrim" onClick={() => setSelected(null)} />
            {renderPanel()}
          </>
        )}

        <div className="sch-dock">
          <SaveStatus state={saveState} dirty={dirty} onRetry={saveNow} short />
          <button type="button" className="op-btn" onClick={undoLast} disabled={!undo.length || saveState === 'conflict'} aria-label="Undo">
            <Msr>undo</Msr>
          </button>
          <button type="button" className="op-btn" onClick={exportCsv} disabled={!order.length} aria-label="Export CSV">
            <Msr>download</Msr>CSV
          </button>
        </div>
      </div>
    </>
  );
};

// One editor per event: a different event id gives a fresh editor, so no
// board, revision or save of one event can reach another (admin review R1-F2).
const OireachtasSchedule = () => {
  const { id } = useParams();
  return <ScheduleEditor key={id || 'none'} eventId={id} />;
};

export default OireachtasSchedule;
